import { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { AnyExtension, Editor, JSONContent, getExtensionField } from '@tiptap/react';
import type { ComponentType } from 'react';

/**
 * What a node's static component receives: the node, read-only, and its extension's options. No editor, view
 * or position: in static rich text the node belongs to no live editor (the editor that rendered the HTML has
 * moved on to another doc). A node view that needs the editor — to select, edit or measure itself — declares
 * no `staticNodeView`, and its docs stay live.
 *
 * `node` is satisfied by a ProseMirror node, which is also what Tiptap 3's `@tiptap/static-renderer` hands a
 * node mapping, so a static component ports as
 * `nodeMapping: { [name]: ({ node }) => createElement(staticNodeView, { node, options: extension.options }) }`.
 */
export type StaticNodeViewProps<Options = unknown> = {
  node: {
    readonly attrs: Readonly<Record<string, unknown>>;
    readonly textContent: string;
    readonly type: { readonly name: string };
  };
  options: Readonly<Options>;
};

export type StaticNodeViewComponent = ComponentType<StaticNodeViewProps>;

declare module '@tiptap/core' {
  // A merged declaration must repeat Tiptap's type parameters, used or not.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface NodeConfig<Options, Storage> {
    /**
     * Renders this node in static (read-only, editor-less) rich text — see staticRender.ts. It pairs with the
     * `addNodeView` declared at the same `.extend()` level: a child that overrides `addNodeView` without its
     * own `staticNodeView` renders live. A node with a React node view but no `staticNodeView` keeps its
     * whole doc on the live-editor path.
     */
    staticNodeView?: ComponentType<StaticNodeViewProps<Options>>;
  }
}

/**
 * The static component of a node extension, or null when it has none. Read from the nearest `.extend()`
 * level that declares `addNodeView` or `staticNodeView`, so a child's own node view never pairs with a
 * parent's static component (`getExtensionField` alone would inherit it).
 */
export function resolveStaticNodeView(extension: AnyExtension): StaticNodeViewComponent | null {
  for (let level: AnyExtension | null = extension; level; level = level.parent) {
    const config = level.config as { addNodeView?: unknown; staticNodeView?: StaticNodeViewComponent };
    if (config.staticNodeView !== undefined) return config.staticNodeView;
    if (config.addNodeView !== undefined) return null;
  }
  return null;
}

// The editor's extension list, as Tiptap's ExtensionManager.resolve builds it (not exported in Tiptap 2):
// kits flattened through `addExtensions`, then a stable sort by descending priority.
const flatten = (extensions: AnyExtension[]): AnyExtension[] =>
  extensions.flatMap(extension => {
    const context = { name: extension.name, options: extension.options, storage: extension.storage };
    const addExtensions = getExtensionField<(() => AnyExtension[]) | undefined>(extension, 'addExtensions', context);
    return addExtensions ? [extension, ...flatten(addExtensions())] : [extension];
  });
const priorityOf = (extension: AnyExtension) => getExtensionField<number | undefined>(extension, 'priority') || 100;

type NodeViewEntry = { component: StaticNodeViewComponent | null; extension: AnyExtension };
const nodeViewSets = new WeakMap<AnyExtension[], ReadonlyMap<string, NodeViewEntry>>();

/**
 * The node types an editor built from `extensions` draws with a node view, each with its static component
 * (null = live only). For a repeated name the last extension with a node view wins, as in the editor's
 * `extensionManager.nodeViews`.
 */
export function nodeViewSet(extensions: AnyExtension[]): ReadonlyMap<string, NodeViewEntry> {
  const cached = nodeViewSets.get(extensions);
  if (cached) return cached;
  const set = new Map<string, NodeViewEntry>();
  for (const extension of flatten(extensions).sort((a, b) => priorityOf(b) - priorityOf(a))) {
    if (extension.type !== 'node' || !getExtensionField(extension, 'addNodeView')) continue;
    set.set(extension.name, { component: resolveStaticNodeView(extension), extension });
  }
  nodeViewSets.set(extensions, set);
  return set;
}

type DocNode = JSONContent | ProseMirrorNode;
const typeName = (node: DocNode) => (node instanceof ProseMirrorNode ? node.type.name : node.type);
const childrenOf = (node: DocNode): readonly DocNode[] =>
  node instanceof ProseMirrorNode ? node.children : node.content ?? [];

export type NodeViewResolution<T extends DocNode> =
  // `none`: no node in the doc has a node view. `static`: every one has a static component, listed in
  // document order. `live`: at least one needs a live editor.
  | {
      mode: 'none' | 'static';
      nodeViews: Array<{ component: StaticNodeViewComponent; extension: AnyExtension; node: T }>;
    }
  | { mode: 'live' };

/**
 * How a doc's node views can render with these extensions. Accepts ProseMirror JSON or a parsed node; only a
 * parsed node carries the attribute defaults a static component reads. A node view's own content is not
 * inspected: a node view nested inside another renders inside it.
 */
export function resolveNodeViews<T extends DocNode>(doc: T, extensions: AnyExtension[]): NodeViewResolution<T> {
  const set = nodeViewSet(extensions);
  const nodeViews: Array<{ component: StaticNodeViewComponent; extension: AnyExtension; node: T }> = [];
  const visit = (node: T): boolean => {
    const name = typeName(node);
    const entry = name === undefined ? undefined : set.get(name);
    if (!entry) return (childrenOf(node) as T[]).every(visit);
    if (!entry.component) return false;
    nodeViews.push({ component: entry.component, extension: entry.extension, node });
    return true;
  };
  if (!visit(doc)) return { mode: 'live' };
  return { mode: nodeViews.length ? 'static' : 'none', nodeViews };
}

/**
 * Lets an editor run `createNodeViews` once, at construction, and hands every later call — EditorContent's
 * post-mount redraw — to `onRedraw`, or drops it when there is none. The redraw re-creates every node view
 * and, in Chrome, reads the DOM selection: a forced full-page layout per editor.
 */
export function guardNodeViewRedraws(editor: Editor, onRedraw?: (redraw: () => void) => void) {
  const createNodeViews = editor.createNodeViews.bind(editor);
  let constructed = false;
  editor.createNodeViews = () => {
    if (!constructed) {
      constructed = true;
      createNodeViews();
      return;
    }
    onRedraw?.(createNodeViews);
  };
}
